// The one line under a Setup page's title: Riverstone's (LEDE), Blueprint's where it says more (BP_LEDE,
// the setup audit, 24 Oct), and the pages only Blueprint has. No stylesheets: Settings.tsx and the pages
// on the SettingsPage pattern both read it.
import type { SettingsPage } from './parts.ts';

// Blueprint's own pages, and its words where a page now holds more than Riverstone's tab (the setup audit)
export type BlueprintPage =
  'booking' | 'plans' | 'invoices' | 'costs' | 'payroll' | 'reviews' | 'agents' | 'connections';
export const BP_LEDE: Partial<Record<SettingsPage, string>> & Record<BlueprintPage, string> = {
  roles:
    'What each role allows, who holds it, and how people sign in. Costs, margins and payroll go in roles only the owner gives.',
  time: 'The company’s working hours, its holidays, and the codes for time that isn’t on a job.',
  devices: 'Every iPad paired here, and how the iPad behaves on the job: what it keeps, when it locks.',
  pricebook: 'What techs quote from, and how far they may go on price on their own. Our cost is owner-only.',
  org: 'The kinds of job you book, the departments they belong to, and the licences each needs.',
  templates: 'The words customers get, what goes out on its own, and when nothing does.',
  calls: 'Why calls didn’t book, what happens when one is missed, and how long recordings are kept.',
  booking: 'How a job is booked, when dispatch warns, and when a technician is on the way and arrives.',
  plans: 'The plans you sell, and how memberships renew: the reminder, and what a declined card does.',
  invoices: 'What a visit charges on its own, how customers pay, and what they sign.',
  costs: 'The margin you aim for, how the huddle counts revenue, and what the warehouse orders on its own.',
  payroll: 'The rules pay is worked out by: sick days, statutory holidays, overtime and late-typed times.',
  reviews: 'Who is asked for a review and when, and who hears about a low rating.',
  agents: 'What each AI agent may do on its own, what it may spend, and the notes it keeps.',
  connections: 'The systems Flowology works with for you: where the books are exported to.',
};

// The one line under the title, for each tab (the screen head, as in the settings prototype).
export const LEDE: Record<Exclude<SettingsPage, BlueprintPage>, string> = {
  rules: 'The company-wide rules behind booking, messages, reviews, prices and recordings.',
  roles:
    'What each role allows, and who holds it. A person may hold several roles and may do what any of them allows.',
  people:
    'Everyone who works here, the roles they sign in with, where those roles apply, and what they add up to.',
  devices:
    'Every iPad paired here: who has it, when it last synced, and what it hasn’t sent. Sign one out if it’s lost.',
  pricebook: 'What techs quote from: prices, member prices and what’s offered. Our cost is owner-only.',
  org: 'Departments and the kinds of job you book, with the rules each department sets.',
  tax: 'The sales tax invoices charge, by zone, with each rate from the date it starts.',
  templates: 'The words customers get in texts and emails.',
  forms: 'The forms techs fill in on the iPad, and the jobs that ask for each.',
  calls: 'Why calls didn’t book: each reason a lead or not, excused or not, for the booking rate.',
  time: 'Who’s off soon, the company’s holidays, and the codes for time that isn’t on a job.',
  campaigns:
    'Where calls and work come from: your campaigns, and the tracking numbers that credit calls to each.',
};
